import { useEffect } from "react";

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Traps focus inside a container (e.g. modal). Only the top modal should trap focus.
 *
 * @param {React.RefObject<HTMLElement | null>} ref - Ref to the trap container
 * @param {boolean} isActive - Whether the trap is active (e.g. isTop for modals)
 */
export const useFocusTrap = (ref, isActive) => {
  useEffect(() => {
    if (!isActive || !ref.current) return;

    const element = ref.current;
    // Controls may be added or enabled after an asynchronous request completes.
    const getFocusableElements = () => Array.from(
      element.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter((el) => !el.matches(":disabled") && el.tabIndex >= 0 && el.offsetParent !== null);

    const handleTabKey = (e) => {
      if (e.key !== "Tab") return;
      const focusableElements = getFocusableElements();
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement) {
        e.preventDefault();
        element.focus();
        return;
      }

      if (e.shiftKey && (document.activeElement === firstElement || !focusableElements.includes(document.activeElement))) {
        lastElement.focus();
        e.preventDefault();
      } else if (!e.shiftKey && (document.activeElement === lastElement || !focusableElements.includes(document.activeElement))) {
        firstElement.focus();
        e.preventDefault();
      }
    };

    element.addEventListener("keydown", handleTabKey);

    // Only move focus if it's currently outside the trap (don't steal intentional focus)
    if (!element.contains(document.activeElement)) {
      (getFocusableElements()[0] || element).focus();
    }

    return () => element.removeEventListener("keydown", handleTabKey);
  }, [ref, isActive]);
};
